import { flatten } from 'lodash';
import { IntlShape } from 'react-intl';

import { AvailableLineDataRefNode } from '@futrli-sdk/predict';

import { forceLocalDate } from '../../../../../utils/dates';
import { ASSUMPTION_TYPES, LAST_DAY_OF_MONTH, LAST_DAY_OF_MONTH_NUM } from '../../../../../core/constants';
import { PROFIT_LOSS_ID, BALANCE_SHEET_ID, NON_FINANCIAL_DRIVERS_ID } from '../../../components/formula/const';
import {
  AvailableLineDataRefNodeWithDepth,
  NodeStaticId,
  PaymentTreatment,
  RepeatingPattern
} from '../../../types';
import { deLocalizeFormula, validateLocalizedFormula } from '../../../../../utils/formulas/i18n-utils';

interface OutputOccursOnData {
  output_occurs_on_day?: number;
  output_occurs_on_month?: number;
}

const getStaticClassifierName = (
  node: AvailableLineDataRefNode,
  nodeStaticIds: Record<NodeStaticId, string>
): string => {
  if (node?.static_id) {
    return nodeStaticIds[node.static_id];
  }
  return node.name;
};

const getAccountNameWithActiveSwitchRule = (
  node: AvailableLineDataRefNode,
  nodeStaticIds: Record<NodeStaticId, string>
): string => {
  if (node.account_type) {
    return `${node.name} [${nodeStaticIds[node.account_type]}]`;
  }
  return node.name
};

const getAiAssumptionName = (node: AvailableLineDataRefNode, intl: IntlShape): string =>
  intl.formatMessage(
    {
      id: 'predict.common.ai_assumption_name',
      defaultMessage: 'Predicted ({primaryAccountName})'
    },
    { primaryAccountName: node.primary_account_name }
  );

/**
 * Recursive function for getting mentions data in flatten array so it can be display in mentions dropdown
 * @param ar initial array of nested data
 * @param depth children depth, we need depth so we can create nested structure in mentions dropdown
 */
export const getFormulaMentionsData = (
  intl: IntlShape,
  nodeStaticIds: Record<NodeStaticId, string>,
  ar: AvailableLineDataRefNode[],
  depth = 0
): AvailableLineDataRefNodeWithDepth[] => {
  const newArr = [];
  for (const a of ar) {
    if (a.children.length > 0) {
      const newData = getFormulaMentionsData(intl, nodeStaticIds, a.children, depth + 1);
      newArr.push([
        {
          ref: a.ref,
          name: a.has_active_switch_rule
            ? getAccountNameWithActiveSwitchRule(a, nodeStaticIds)
            : a.classifier === 'static'
            ? getStaticClassifierName(a, nodeStaticIds)
            : a.assumption_type === ASSUMPTION_TYPES.AI
            ? getAiAssumptionName(a, intl)
            : a.name,
          children: a.children,
          depth,
          disabled: a.disabled,
          classifier: a.classifier
        },
        ...newData
      ]);
    } else {
      newArr.push({
        ref: a.ref,
        name: a.has_active_switch_rule
          ? getAccountNameWithActiveSwitchRule(a, nodeStaticIds)
          : a.classifier === 'static'
          ? getStaticClassifierName(a, nodeStaticIds)
          : a.assumption_type === ASSUMPTION_TYPES.AI
          ? getAiAssumptionName(a, intl)
          : a.name,
        children: [],
        depth,
        disabled: a.disabled,
        classifier: a.classifier
      });
    }
  }
  return flatten(newArr) as AvailableLineDataRefNodeWithDepth[];
};

/**
 * Receives date and returns true if date is in past regards to current time
 * @param dateToCompare date to check if it is in past
 * @param isLastDayOfMonth comparing with is last day of month options
 */
export const checkIfIsInPast = (dateToCompare: Date, isLastDayOfMonth = false, forecastFromDateStr?: string | null) => {
  const forecastFromDate = forceLocalDate(forecastFromDateStr || '');

  if (isLastDayOfMonth) {
    if (
      dateToCompare.getFullYear() === forecastFromDate.getFullYear() &&
      dateToCompare.getMonth() === forecastFromDate.getMonth()
    ) {
      return false;
    }
  }

  if (
    dateToCompare.getDate() === forecastFromDate.getDate() &&
    dateToCompare.getFullYear() === forecastFromDate.getFullYear() &&
    dateToCompare.getMonth() === forecastFromDate.getMonth()
  ) {
    return false;
  }

  return dateToCompare.getTime() < forecastFromDate.getTime();
};

export const getLastDayOfMonth = (month: number, outputFrequency: RepeatingPattern) => {
  if (outputFrequency === RepeatingPattern.monthly) {
    return LAST_DAY_OF_MONTH_NUM;
  }
  const currentYear = new Date().getFullYear();
  const lastDayOfMonth = new Date(currentYear, month, 0);
  return lastDayOfMonth.getDate();
};

/**
 * Get formula assumption content for preview route
 */
export const getFormulaDataContent = (argObj: {
  predictionName: string;
  vatPercentage: string;
  formulaContent: string;
  accountIndex: string;
  outputFrequency: RepeatingPattern;
  formulaFrequencyOnDay: string;
  formulaFrequencyOnMonth: string;
  paymentTreatment: PaymentTreatment;
}) => {
  const data = {
    name: argObj.predictionName,
    vat_percentage: argObj.paymentTreatment === PaymentTreatment.NON_CASH_TRANSFER ? '0' : argObj.vatPercentage || null,
    account_id: argObj.accountIndex,
    excluded_from_report: false,
    formula: deLocalizeFormula(argObj.formulaContent),
    output_frequency: argObj.outputFrequency,
    ...getOutputOccursOnData(argObj.outputFrequency, argObj.formulaFrequencyOnDay, argObj.formulaFrequencyOnMonth)
  };

  if (argObj.outputFrequency === RepeatingPattern.daily) {
    delete data.output_occurs_on_day;
    delete data.output_occurs_on_month;
  }
  if (argObj.outputFrequency === RepeatingPattern.weekly || argObj.outputFrequency === RepeatingPattern.monthly) {
    delete data.output_occurs_on_month;
  }

  return data;
};

export const getFormulaOutputFrequency = (outputFrequency: RepeatingPattern) => {
  switch (outputFrequency) {
    case RepeatingPattern.monthly: {
      return 'MONTHLY';
    }
    case RepeatingPattern.daily: {
      return 'DAILY';
    }
    default: {
      return 'ANNUALLY';
    }
  }
};

export const checkFormulaValidation = (content: string, outputFrequency: RepeatingPattern) => {
  // Just to check if formula contains any words that are not in current language, for example
  // formula contains SUM and user language is French
  const isValidLocalization = validateLocalizedFormula(content);
  if (!isValidLocalization) {
    return false;
  }
  const formulaOutputFrequency = getFormulaOutputFrequency(outputFrequency);
  const { IsValid } = window.validateFormula
    ? window.validateFormula(deLocalizeFormula(content), formulaOutputFrequency)
    : { IsValid: true };
  return IsValid;
};

/**
 * Getting data from mentions menu (showed after pressing @ inside formula editor)
 * @param profitAndLoss
 * @param balanceSheet
 * @returns
 */
export const getAvailableLineData = (
  intl: IntlShape,
  nodeStaticIds: Record<NodeStaticId, string>,
  profitAndLoss: AvailableLineDataRefNode[],
  profitAndLossName: string,
  balanceSheet: AvailableLineDataRefNode[],
  balanceSheetName: string,
  nonFinancialDrivers: AvailableLineDataRefNode[],
  driversName: string,
  showOnlyProfitAndLoss = false
) => {
  const updatedAvailableLineData = [
    {
      disabled: false,
      ref: PROFIT_LOSS_ID,
      name: profitAndLossName,
      children: [...profitAndLoss],
      classifier: 'static' as const
    },
    {
      disabled: false,
      ref: BALANCE_SHEET_ID,
      name: balanceSheetName,
      children: [...balanceSheet],
      classifier: 'static' as const
    }
  ];

  if (showOnlyProfitAndLoss) {
    updatedAvailableLineData.pop();
  }

  updatedAvailableLineData.push({
    disabled: false,
    ref: NON_FINANCIAL_DRIVERS_ID,
    name: driversName,
    children: [...nonFinancialDrivers],
    classifier: 'static' as const
  });

  const initialOptions = getFormulaMentionsData(
    intl,
    nodeStaticIds,
    updatedAvailableLineData as AvailableLineDataRefNode[]
  );

  const parsedInitialOptions = initialOptions.map(o => ({
    id: o.ref,
    name: o.name,
    depth: o.depth,
    disabled: o.disabled
  }));

  return parsedInitialOptions;
};

export const getOutputOccursOnData = (
  outputFrequency: RepeatingPattern,
  frequencyOnDay: string,
  frequencyOnMonth: string
) => {
  const output: OutputOccursOnData = {
    output_occurs_on_day: frequencyOnDay === LAST_DAY_OF_MONTH ? LAST_DAY_OF_MONTH_NUM : Number(frequencyOnDay),
    output_occurs_on_month: Number(frequencyOnMonth)
  };

  switch (outputFrequency) {
    case RepeatingPattern.daily: {
      delete output.output_occurs_on_day;
      delete output.output_occurs_on_month;
      break;
    }
    case RepeatingPattern.weekly:
    case RepeatingPattern.monthly: {
      delete output.output_occurs_on_month;
      break;
    }
    default: {
      // annually or quarterly - leave data in-tact
    }
  }

  return output;
};
